import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useAuth } from "../../../contexts/AuthContext";
import { APP_SHELL_LAYOUT } from "../../../constants/constants";
import {
  createRaffleTicket,
  findClaimForCampaign,
  getClaimAlreadyClaimedMessage,
  getClaimTicketPath,
  getPublicRaffleById,
  isAlreadyClaimedResult,
  listMyRaffleClaims,
} from "../../../services/claimsService";
import { followCampaignBusiness } from "../../../services/claimFollowService";
import { getClaimHeroImageAspectRatioCss } from "../../../utils/claimHeroImage";
import { getSuccessContainedDarkTextSx } from "./claimActionStyles";
import ClaimFixedAction from "./ClaimFixedAction";
import OfficialRulesDisclosure from "./OfficialRulesDisclosure";

function toTimeMs(value) {
  if (!value) return 0;
  const parsed = new Date(value);
  const timeMs = parsed.getTime();
  return Number.isFinite(timeMs) ? timeMs : 0;
}

function formatWindow(value) {
  const timeMs = toTimeMs(value);
  if (!timeMs) return "Not set";
  return new Date(timeMs).toLocaleString();
}

function resolveBusinessName(business = {}) {
  return String(business?.companyName || business?.displayName || business?.name || "").trim();
}

export default function ClaimLandingPage({ campaignType = "raffle" }) {
  const { campaignId } = useParams();
  const { currentUser } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const followedBusinessRef = React.useRef(new Set());
  const [campaign, setCampaign] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [claiming, setClaiming] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [infoMessage, setInfoMessage] = React.useState("");
  const [existingClaimPath, setExistingClaimPath] = React.useState("");
  const [existingClaim, setExistingClaim] = React.useState(null);
  const [redirectingToExistingClaim, setRedirectingToExistingClaim] = React.useState(false);

  const resolvedCampaignType = campaign?.campaignType || campaignType;
  const isCoupon = resolvedCampaignType === "coupon";
  const noun = isCoupon ? "coupon" : "raffle ticket";
  const claimStartMs = toTimeMs(campaign?.claimStartAt);
  const claimEndMs = toTimeMs(campaign?.claimEndAt);
  const nowMs = Date.now();
  const claimNotStarted = claimStartMs > 0 && nowMs < claimStartMs;
  const claimEnded = claimEndMs > 0 && nowMs > claimEndMs;
  const claimUnavailable = claimNotStarted || claimEnded;
  const businessName = resolveBusinessName(campaign?.business || {});
  const businessInitial = businessName ? businessName.charAt(0).toUpperCase() : "I";
  const heroImageUrl = String(campaign?.heroImageUrl || "").trim();
  const heroImageAspectRatio = getClaimHeroImageAspectRatioCss(campaign?.heroImage);
  const governingTermsText = isCoupon
    ? String(campaign?.couponTerms?.text || "").trim()
    : String(campaign?.officialRules?.text || "").trim();
  const governingTermsTitle = isCoupon ? "Coupon Terms" : "Official Rules";
  const governingTermsHelperText = isCoupon ? "View coupon terms" : "View raffle fine print";
  const claimButtonDisabled = !campaign?.id || claiming || claimUnavailable || Boolean(existingClaim);
  const claimButtonLabel = existingClaim
    ? "Already Claimed"
    : claiming
      ? "Claiming..."
      : currentUser
        ? `Claim ${isCoupon ? "Coupon" : "Ticket"}`
        : "Sign In to Claim";

  React.useEffect(() => {
    if (currentUser) return;
    const next = `${location.pathname}${location.search || ""}`;
    navigate(`/signin?next=${encodeURIComponent(next)}`, { replace: true });
  }, [currentUser, location.pathname, location.search, navigate]);

  React.useEffect(() => {
    let cancelled = false;
    async function loadCampaign() {
      if (!currentUser?.uid) return;
      setLoading(true);
      setErrorMessage("");
      setInfoMessage("");
      setExistingClaimPath("");
      setExistingClaim(null);
      setRedirectingToExistingClaim(false);
      try {
        const nextCampaign = await getPublicRaffleById({ campaignId, campaignType });
        let matchedClaim = null;
        if (currentUser?.uid && nextCampaign?.id) {
          try {
            const claims = await listMyRaffleClaims();
            matchedClaim = findClaimForCampaign(claims, nextCampaign);
          } catch (claimsError) {
            console.warn("Unable to check existing claims:", claimsError);
          }
        }
        if (!cancelled) {
          setCampaign(nextCampaign);
          if (matchedClaim) {
            const matchedClaimPath = getClaimTicketPath(matchedClaim);
            if (matchedClaimPath) {
              setRedirectingToExistingClaim(true);
              navigate(matchedClaimPath, { replace: true });
              return;
            }
            setExistingClaim(matchedClaim);
            setInfoMessage(getClaimAlreadyClaimedMessage(matchedClaim));
            setExistingClaimPath("");
          }
        }
      } catch (error) {
        if (!cancelled) setErrorMessage(error?.message || "Unable to load this claim.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadCampaign();
    return () => {
      cancelled = true;
    };
  }, [campaignId, campaignType, currentUser?.uid, navigate]);

  React.useEffect(() => {
    const businessId = String(campaign?.business?.id || campaign?.businessId || "").trim();
    if (!currentUser?.uid || !businessId || followedBusinessRef.current.has(businessId)) return;
    followedBusinessRef.current.add(businessId);
    followCampaignBusiness(currentUser.uid, campaign).catch((error) => {
      followedBusinessRef.current.delete(businessId);
      console.warn("Unable to follow claim business:", error);
    });
  }, [campaign, currentUser?.uid]);

  const handleClaim = async () => {
    if (!currentUser) {
      const next = `${location.pathname}${location.search || ""}`;
      navigate(`/signin?next=${encodeURIComponent(next)}`, { replace: true });
      return;
    }

    setClaiming(true);
    setErrorMessage("");
    setInfoMessage("");
    setExistingClaimPath("");
    try {
      const result = await createRaffleTicket({
        raffleId: campaign?.id || campaignId,
        campaignType: resolvedCampaignType,
        // Attribution carried from a capsule CTA (§3.5 4b), if that's how we got here.
        claimSource: location.state?.claimSource || undefined,
        referralCampaignId: location.state?.referralCampaignId || undefined,
      });
      if (isAlreadyClaimedResult(result)) {
        setInfoMessage(getClaimAlreadyClaimedMessage({
          ...result,
          campaignType: result?.raffle?.campaignType || resolvedCampaignType,
        }));
        setExistingClaimPath(getClaimTicketPath({
          campaignType: result?.raffle?.campaignType || resolvedCampaignType,
          ticketToken: result?.ticket?.ticketToken || result?.ticketToken,
        }));
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("inssa:claims-updated"));
        }
        return;
      }
      await followCampaignBusiness(currentUser.uid, result?.raffle || campaign);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("inssa:claims-updated"));
      }
      const ticketToken = result?.ticket?.ticketToken;
      window.setTimeout(() => {
        navigate(
          getClaimTicketPath({
            campaignType: result?.raffle?.campaignType || resolvedCampaignType,
            ticketToken,
          }),
          {
            replace: true,
            state: isCoupon
              ? {
                  claimCelebration: {
                    campaignType: "coupon",
                    ticketToken,
                  },
                }
              : undefined,
          }
        );
      }, 550);
    } catch (error) {
      setErrorMessage(error?.message || `Unable to claim this ${noun}.`);
    } finally {
      window.setTimeout(() => setClaiming(false), 650);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        px: { xs: 2, sm: 3 },
        pt: { xs: 2, sm: 3 },
        pb: `calc(${APP_SHELL_LAYOUT.BOTTOM_NAV_OFFSET_CSS} + 128px)`,
        display: "grid",
        placeItems: "start center",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 560,
          borderRadius: 2,
          overflow: "hidden",
          border:
            theme.palette.mode === "dark"
              ? `1px solid ${alpha(theme.palette.common.white, 0.16)}`
              : `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 18px 58px ${alpha(theme.palette.common.black, 0.34)}`
              : "0 16px 48px rgba(42,55,105,0.1)",
        }}
      >
        <CardContent sx={{ p: { xs: 2.25, sm: 3 } }}>
          {loading || redirectingToExistingClaim ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 7 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
              {infoMessage ? (
                <Alert
                  severity="info"
                  action={
                    existingClaimPath ? (
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => navigate(existingClaimPath)}
                      >
                        View {isCoupon ? "Coupon" : "Ticket"}
                      </Button>
                    ) : null
                  }
                >
                  {infoMessage}
                </Alert>
              ) : null}
              {claimNotStarted ? <Alert severity="info">Claims open on {formatWindow(campaign?.claimStartAt)}.</Alert> : null}
              {claimEnded ? <Alert severity="warning">This {isCoupon ? "coupon" : "raffle"} is no longer accepting claims.</Alert> : null}

              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar
                  src={String(campaign?.business?.avatarUrl || "").trim() || undefined}
                  sx={{ bgcolor: alpha(theme.palette.success.main, 0.18), color: theme.palette.success.main, fontWeight: 800 }}
                >
                  {businessInitial}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>
                    {businessName || "Inssa"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isCoupon ? "Digital coupon" : "Digital raffle"}
                  </Typography>
                </Box>
              </Stack>

              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.05, letterSpacing: 0 }}>
                {campaign?.title || (isCoupon ? "Coupon unavailable" : "Raffle unavailable")}
              </Typography>
              <Typography color="text.secondary">
                {campaign?.description || `This ${isCoupon ? "coupon" : "raffle"} is not currently available.`}
              </Typography>

              <OfficialRulesDisclosure
                text={governingTermsText}
                title={governingTermsTitle}
                helperText={governingTermsHelperText}
              />

              {heroImageUrl ? (
                <Box
                  component="img"
                  src={heroImageUrl}
                  alt={campaign?.title || noun}
                  sx={{ width: "100%", aspectRatio: heroImageAspectRatio, objectFit: "cover", borderRadius: 1.5 }}
                />
              ) : null}

              <Typography variant="body2" color="text.secondary">
                {claimUnavailable
                  ? `Claim window: ${formatWindow(campaign?.claimStartAt)} to ${formatWindow(campaign?.claimEndAt)}`
                  : isCoupon
                    ? "Claim this offer, then show your pass to staff when you are ready to redeem."
                    : "Claim your entry, then keep the ticket handy for results and redemption."}
              </Typography>

            </Stack>
          )}
        </CardContent>
      </Card>
      {!loading && !redirectingToExistingClaim ? (
        <ClaimFixedAction testId="claim-fixed-action" maxWidth={560}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleClaim}
            disabled={claimButtonDisabled}
            sx={(theme) => ({
              py: 1.35,
              fontWeight: 900,
              ...getSuccessContainedDarkTextSx(theme),
            })}
          >
            {claimButtonLabel}
          </Button>
        </ClaimFixedAction>
      ) : null}
    </Box>
  );
}
